/**
 * Browser-side pixel and analytics event pipeline.
 *
 * Lifecycle of a pixel:
 * 1. Server handlers call `add_pixel`, and `head-analytics.html` renders the
 *    resulting signed payloads as `window.storyworthPixels` (data layer + CAPI)
 *    and `window.storyworthCapiOnlyPixels` (CAPI only).
 * 2. `initializeAnalytics()` runs at module load, syncs Google Consent Mode
 *    from OneTrust, and waits for the OneTrust advertising group (C0004)
 *    before dispatching anything.
 * 3. `dispatchPixels()` pushes a `storyworth.pixel` event per pixel to
 *    `window.dataLayer`, which GTM reads through its `DLV - sw_*` variables,
 *    and relays server-signed pixels to `/capi` for the Conversions API.
 *    Client-only pixels (no server signature) are dispatched with
 *    `{ capi: false }`.
 *
 * Analytics events (`GAEvents.enqueueGAEvent`) are separate
 * `storyworth.analytics_event` pushes whose extra parameters live under
 * `sw_params`.
 *
 * The consent, cookie, click-id, data-layer, and utils modules here are also
 * bundled into the Webflow marketing site's scripts (js/webflow/ → served at
 * static/webflow/*.js) — changes to them reach welcome.storyworth.com within
 * minutes of a deploy, without any Webflow-side step.
 */
import { dispatchCapi, resetCapiForTests } from "./capi"
import { initializeClarityConsentSync, resetClarityForTests } from "./clarity"
import {
  activeConsentGroups,
  hasAdvertisingConsent,
  hasAnalyticsConsent,
  hasMeasurementConsent,
  initializeConsentSync,
  resetConsentForTests,
  runAfterAdvertisingConsent,
  runAfterAnalyticsConsent,
  runAfterMeasurementConsent,
} from "./consent"
import {
  dispatchDataLayerPixel,
  ensureEventId,
  pushDataLayerEvent,
  pushDataLayerEventAfterConsent,
  resetDataLayerPixelsForTests,
} from "./data_layer"
import { saveImpactClickId } from "./impact"
import {
  initializeIntercomConsentSync,
  resetIntercomForTests,
} from "./intercom"
import { saveUtmParamsAfterAnalyticsConsent } from "./utm"
import type { AnalyticsEventExtra, DispatchOptions, PixelMap } from "./types"
import { withoutUndefined } from "./utils"

export {
  activeConsentGroups,
  hasAdvertisingConsent,
  hasAnalyticsConsent,
  hasMeasurementConsent,
  initializeConsentSync,
  pushDataLayerEvent,
  pushDataLayerEventAfterConsent,
  runAfterAdvertisingConsent,
  runAfterMeasurementConsent,
}
export type {
  AnalyticsEventExtra,
  DataLayerEvent,
  DispatchOptions,
  PixelEventData,
  PixelMap,
} from "./types"

export const GAEvents = {
  /**
   * Pushes a `storyworth.analytics_event` for GTM once analytics consent
   * (C0002) is granted. Extra parameters are captured when enqueued and
   * nested under `sw_params` (always present) so GTM can forward them as one
   * Data Layer Variable. That variable is configured as Data Layer Version 1
   * in the container so each push replaces the object outright instead of
   * merging into the previous event's params.
   */
  enqueueGAEvent(
    eventName: string,
    eventCategory = "flow",
    extra: AnalyticsEventExtra = null,
  ) {
    const event = {
      event: "storyworth.analytics_event",
      sw_event_name: eventName,
      sw_event_category: eventCategory,
      sw_params: withoutUndefined(extra || {}),
    }
    runAfterAnalyticsConsent(() => pushDataLayerEvent(event))
  },
}

export function initializeAnalytics(pixels?: PixelMap) {
  installDataLayerGlobals()

  if (window.storyworthAnalyticsInitialized) {
    return
  }
  window.storyworthAnalyticsInitialized = true

  const initialPixels = pixels ?? window.storyworthPixels ?? {}
  const capiOnlyPixels = window.storyworthCapiOnlyPixels ?? {}

  initializeConsentSync()
  initializeClarityConsentSync()
  initializeIntercomConsentSync()
  runAfterAdvertisingConsent(saveImpactClickId)
  saveUtmParamsAfterAnalyticsConsent()

  if (!initialPixels.page_view) {
    dispatchPixelsAfterConsent(
      {
        page_view: {
          event_source_url: window.location.href,
        },
      },
      { capi: false },
    )
  }
  if (Object.keys(initialPixels).length > 0) {
    dispatchPixelsAfterConsent(initialPixels)
  }
  if (Object.keys(capiOnlyPixels).length > 0) {
    dispatchPixelsAfterConsent(capiOnlyPixels, {
      capi: true,
      dataLayer: false,
    })
  }
}

/**
 * Dispatches pixels once the visitor has consented: the data-layer push waits
 * for analytics or advertising consent (GTM tags then apply their own
 * per-tag consent), while the Conversions API relay waits for advertising
 * consent specifically.
 */
export function dispatchPixelsAfterConsent(
  pixels: PixelMap = {},
  { capi = true, dataLayer = true }: DispatchOptions = {},
) {
  Object.values(pixels).forEach(ensureEventId)

  if (dataLayer) {
    runAfterMeasurementConsent(() =>
      dispatchPixels(pixels, { capi: false, dataLayer: true }),
    )
  }
  if (capi) {
    runAfterAdvertisingConsent(() =>
      dispatchPixels(pixels, { capi: true, dataLayer: false }),
    )
  }
}

export function dispatchPixels(
  pixels: PixelMap = {},
  { capi = true, dataLayer = true }: DispatchOptions = {},
) {
  Object.values(pixels).forEach(ensureEventId)

  if (dataLayer) {
    Object.entries(pixels).forEach(([pixel, data]) => {
      dispatchDataLayerPixel(pixel, data)
    })
  }

  if (capi) {
    dispatchCapi(pixels)
  }
}

export function resetAnalyticsForTests() {
  resetDataLayerPixelsForTests()
  resetCapiForTests()
  resetConsentForTests()
  resetClarityForTests()
  resetIntercomForTests()
  window.storyworthAnalyticsInitialized = false
}

/** Exposes the analytics event helper to inline template scripts. */
function installDataLayerGlobals() {
  window.enqueueGAEvent = GAEvents.enqueueGAEvent
}

if (import.meta.env.MODE !== "test") {
  initializeAnalytics()
}
